import React from 'react';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import { PrimaryButton } from '@magoosh/lib/button';
import CardLayout from '@magoosh/layouts/card';
import TextTabButtons from '@magoosh/lib/tabs/text_tab_buttons';
import StudySchedule from '@magoosh/organisms/study_schedule';
import useSearchQuery from '@magoosh/utils/use_search_query';
import { paths } from 'config/path_helpers';

import ContextualLink from '@test_prep/app/components/contextual_link';
import AITutorNoticeCard from '@test_prep/app/embeds/ai_tutor_dashboard_card';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';

import DeckSetSummary from './components/deck_set_summary';
import ExamSectionSummary from './components/exam_section_summary';
import WootricSurvey from './components/wootric_survey';

import { DashboardUI, ExamSection, FlashcardDeckSet } from './types';

import style from './style.module.scss';

interface Page {
  examSections: ExamSection[];
  flashcardDeckSets: FlashcardDeckSet[];
  ui: DashboardUI;
}

const VIEW_GUIDANCE = 'guidance';
const VIEW_DASHBOARD = 'dashboard';

const Dashboard: React.FC = () => {
  const { branding } = useAppContext().data;
  const history = useHistory();
  const q = useQueries();
  const view = useSearchQuery().view as string || VIEW_DASHBOARD;

  const page: Page | undefined = q.pageQuery(paths.dashboard()).data;

  const breadcrumbs = [
    { name: branding.examName, path: paths.dashboard() },
    { name: 'Home' }
  ];

  const handleViewChanged = (view: string) => {
    const queryParams = new URLSearchParams({ view });
    history.replace(`${paths.dashboard()}?${queryParams.toString()}`);
  };

  const practiceCards = (ui: DashboardUI) => [
    {
      description: 'You choose the type of question. Perfect for targeted practice or a short daily session.',
      href: paths.startPractice(),
      name: 'Custom Practice',
      show: true
    },
    {
      description: ui.practiceCards.sectionTest.description,
      href: paths.practiceTestsStartSection(),
      name: 'Section Test',
      show: ui.practiceCards.sectionTest.show
    },
    {
      description: ui.practiceCards.practiceTest.description,
      href: paths.practiceTestsStart(),
      name: 'Practice Test',
      show: ui.practiceCards.practiceTest.show
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard - Magoosh {branding.examName}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs} loading={!page} notices={page?.ui?.notices}>
        {({ loading }) => {
          if (loading || !page) return;

          return (
            <>
              <div className="visible-xs">
                <div>
                  <TextTabButtons currentTab={view} onChange={handleViewChanged}>
                    <TextTabButtons.Tab name={VIEW_GUIDANCE}>Study Schedule</TextTabButtons.Tab>
                    <TextTabButtons.Tab name={VIEW_DASHBOARD}>Dashboard</TextTabButtons.Tab>
                  </TextTabButtons>

                  <hr className="u-margin-V-xs" />
                </div>
              </div>

              <div className="d-flex gap-l">
                <div className={cx(style.constrainedWidth, { 'hidden-xs': view !== VIEW_GUIDANCE })}>
                  <CardLayout.Card>
                    <div className={style.studyScheduleOverrides}>
                      <StudySchedule />
                    </div>
                  </CardLayout.Card>
                </div>

                <div
                  className={cx('d-flex flex-1 flex-column gap-l', { 'hidden-xs': view !== VIEW_DASHBOARD })}
                  style={{ maxWidth: 730, width: '100%' }}
                >
                  <div className="d-flex flex-column gap-s gap-lg-m">
                    {page.ui.aiTutorNotice.show && <AITutorNoticeCard examName={branding.examName} />}

                    <div className="d-flex flex-column gap-s gap-lg-m">
                      <h3 className="u-margin-A-n"><strong>Practice</strong></h3>
                      <hr className="u-margin-A-n" />
                    </div>

                    <div className="d-flex flex-column flex-lg-row gap-m">
                      {
                        practiceCards(page.ui).filter((card) => card.show).map((card) => (
                          <CardLayout.Card key={card.name} className="d-flex flex-column gap-s">
                            <h4 className="text-center u-margin-A-n">
                              {card.name}
                            </h4>

                            <div className="flex-1 small text-center">
                              {card.description}
                            </div>

                            <ContextualLink
                              className="text-hover-no-decoration text-no-decoration"
                              href={card.href}
                            >
                              <PrimaryButton block>
                                Start
                              </PrimaryButton>
                            </ContextualLink>
                          </CardLayout.Card>
                        ))
                      }
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-s gap-lg-m">
                    <div className="d-flex flex-column gap-s gap-lg-m">
                      <div className="align-items-end d-flex">
                        <h3 className="flex-1 u-margin-A-n"><strong>{page.ui.sectionsHeading}</strong></h3>

                        <div className="align-items-center d-flex gap-xs hidden-xs">
                          <ContextualLink
                            className="small text-gray-light text-hover-no-decoration text-no-decoration"
                            href={paths.reviewAnswers()}
                          >
                            See Detailed Results
                          </ContextualLink>

                          <div className="small text-gray-light">|</div>

                          <ContextualLink
                            className="small text-gray-light text-hover-no-decoration text-no-decoration"
                            href={paths.reset()}
                          >
                            Reset Stats
                          </ContextualLink>
                        </div>
                      </div>

                      <hr className="u-margin-A-n" />
                    </div>

                    <div className="d-flex flex-column gap-m">
                      {
                        page.examSections.map((examSection) => (
                          <CardLayout.Card key={examSection.id} className="flex-1">
                            <ExamSectionSummary { ...examSection } />
                          </CardLayout.Card>
                        ))
                      }

                      {
                        page.flashcardDeckSets.map((deckSet) => (
                          <CardLayout.Card key={deckSet.id} className="flex-1">
                            <DeckSetSummary { ...deckSet } />
                          </CardLayout.Card>
                        ))
                      }

                    </div>
                  </div>
                </div>
              </div>

              {
                page.ui.wootricSurvey.show && (
                  <WootricSurvey { ...page.ui.wootricSurvey.settings } />
                )
              }
            </>
          );
        }}
      </CardLayout>
    </>
  );
};

export default Dashboard;
